import { z } from 'zod';
import { birthDateSchema, optionalInput } from '../api';
import { FACILITY_TYPE, ONLINE_PAYMENT_METHOD, ONLINE_ORDER_STATUS, REQUEST_KIND, REQUEST_STATUS, SEX, } from '../enums';
/**
 * The public directory: how a patient with no account and no hospital finds
 * one, and how what they ask for reaches the right desk.
 *
 * Everything here crosses the tenant boundary on purpose, so the shapes are
 * deliberately thin — a listing carries what a hospital chose to publish and
 * nothing else. No patient record, no clinical field, ever.
 */
const phoneSchema = z
    .string()
    .trim()
    .regex(/^[0-9+\-\s]{7,20}$/, 'Enter a valid phone number');
// ── The listing a hospital publishes about itself ─────────────────────────────
export const openingHoursSchema = z.object({
    /** 0 = Sunday … 6 = Saturday. */
    weekday: z.number().int().min(0).max(6),
    opensAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:mm'),
    closesAt: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:mm'),
});
export const upsertListingSchema = z.object({
    branchId: z.string().uuid(),
    types: z.array(z.enum(FACILITY_TYPE)).min(1, 'Pick at least one'),
    about: optionalInput(z.string().trim().max(1200)),
    services: z.array(z.string().trim().min(1).max(60)).max(30).default([]),
    phone: optionalInput(phoneSchema),
    email: optionalInput(z.string().trim().email()),
    openingHours: z.array(openingHoursSchema).max(14).default([]),
    latitude: z.number().min(-90).max(90).nullable().optional(),
    longitude: z.number().min(-180).max(180).nullable().optional(),
    isPublished: z.boolean().default(false),
});
// ── What the public sees ──────────────────────────────────────────────────────
export const DEFAULT_SEARCH_RADIUS_KM = 20;
export const directoryQuerySchema = z.object({
    city: z.string().trim().max(80).optional(),
    type: z.enum(FACILITY_TYPE).optional(),
    q: z.string().trim().max(80).optional(),
    lat: z.coerce.number().min(-90).max(90).optional(),
    lng: z.coerce.number().min(-180).max(180).optional(),
    radiusKm: z.coerce.number().min(1).max(200).default(DEFAULT_SEARCH_RADIUS_KM),
});
// ── The patient's own account on the public site ──────────────────────────────
export const publicOtpRequestSchema = z.object({ phone: phoneSchema });
export const publicOtpVerifySchema = z.object({
    phone: phoneSchema,
    code: z.string().trim().regex(/^\d{6}$/, 'Six digits'),
    /** Supplied on the first sign-in, when the account is created. */
    name: z.string().trim().min(2).max(120).optional(),
    city: z.string().trim().max(80).optional(),
});
// ── Asking for an appointment ─────────────────────────────────────────────────
export const createAppointmentRequestSchema = z
    .object({
    tenantSlug: z.string().trim().min(1).max(80),
    branchId: z.string().uuid(),
    kind: z.enum(REQUEST_KIND).default('opd'),
    patientName: z.string().trim().min(2).max(120),
    phone: phoneSchema,
    sex: z.enum(SEX).optional(),
    dob: birthDateSchema.optional(),
    doctorId: z.string().uuid().optional(),
    /** The slot the patient picked, if the hospital publishes its diary. */
    requestedSlotAt: z.string().datetime().optional(),
    testIds: z.array(z.string().uuid()).max(30).default([]),
    notes: z.string().trim().max(600).optional(),
})
    .refine((value) => (value.kind === 'opd' ? Boolean(value.doctorId) : value.testIds.length > 0), {
    message: 'Pick a doctor for a clinic visit, or at least one test for a lab visit',
    path: ['doctorId'],
});
export const rejectSchema = z.object({
    reason: z.string().trim().min(3, 'Say why, the patient sees this').max(300),
});
export const approveRequestSchema = z.object({
    /** Staff may move the visit to a slot that actually suits the clinic. */
    slotAt: z.string().datetime().optional(),
    doctorId: z.string().uuid().optional(),
    note: z.string().trim().max(300).optional(),
});
// ── Ordering medicines ────────────────────────────────────────────────────────
export const PAYMENT_PROOF_MAX_BYTES = 5 * 1024 * 1024;
export const PAYMENT_PROOF_CONTENT_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
export const MAX_OPEN_ONLINE_ORDERS = 5;
function parsedUrl(value) {
    try {
        return new URL(value);
    }
    catch {
        return null;
    }
}
export function isHttpsUrl(value) {
    return parsedUrl(value.trim())?.protocol === 'https:';
}
export function isSafeExternalUrl(value) {
    if (!value)
        return false;
    const protocol = parsedUrl(value.trim())?.protocol;
    return protocol === 'https:' || protocol === 'http:';
}
const httpsLinkSchema = z
    .string()
    .trim()
    .max(500)
    .refine(isHttpsUrl, 'Use a link that starts with https://');
export const presignPaymentProofSchema = z.object({
    tenantSlug: z.string().trim().min(1).max(80),
    branchId: z.string().uuid(),
    filename: z.string().trim().min(1).max(200),
    contentType: z.string().trim().min(3).max(120),
    sizeBytes: z.number().int().min(1).max(PAYMENT_PROOF_MAX_BYTES, 'That file is larger than the 5 MB limit'),
});
export const createOnlineOrderSchema = z
    .object({
    tenantSlug: z.string().trim().min(1).max(80),
    branchId: z.string().uuid(),
    customerName: z.string().trim().min(2).max(120),
    phone: phoneSchema,
    address: z.string().trim().min(5).max(400),
    paymentMethod: z.enum(ONLINE_PAYMENT_METHOD).default('cod'),
    paymentProofKey: z.string().trim().min(1).max(300).optional(),
    paymentProofUrl: httpsLinkSchema.optional(),
    paymentRef: z.string().trim().max(80).optional(),
    /** A photo of the prescription, for anything that needs one. */
    prescriptionUrl: httpsLinkSchema.optional(),
    notes: z.string().trim().max(400).optional(),
    items: z
        .array(z.object({
        productId: z.string().uuid(),
        qty: z.number().positive().max(1000),
    }))
        .min(1, 'The basket is empty'),
})
    .refine((value) => value.paymentMethod !== 'transfer' || Boolean(value.paymentProofKey || value.paymentProofUrl), {
    message: 'Attach the transfer slip so the pharmacy can verify it',
    path: ['paymentProofKey'],
});
export const ORDER_STATUS_LABEL = Object.fromEntries(ONLINE_ORDER_STATUS.map((status) => [status, status.replace(/_/g, ' ')]));
export const REQUEST_STATUS_LABEL = Object.fromEntries(REQUEST_STATUS.map((status) => [status, status.replace(/_/g, ' ')]));
//# sourceMappingURL=public-directory.js.map